import { call, put, select, takeEvery } from 'redux-saga/effects'

import { selectExtraRoutesAuthData } from '@/containers/Login/AuthSelectors'
import { callApi } from '@/shared/tools/apiSagaHelper'
import type { PollScore } from '@/shared/tools/communicationBoardPolls'
import { voteFailed, voteRequested, voteSucceeded, votesLoaded } from './boardPollsSlice'
import { selectBoardPollsState } from './boardPollsSelectors'
import type {
  BoardPollRoutes,
  BoardPollsState,
  LoadPollVotesAction,
  MyPollVotesResponse,
  PollApiError,
  PollVoteFailure,
  SendPollVoteAction,
} from './types'

interface ApiRequest {
  endpoint?: string
  method?: string
  payload?: unknown
  queryParams?: string[][]
  showErrorSnackbar?: boolean
  asteriskReplacement?: string
}

const requestApi = callApi as unknown as (request: ApiRequest) => Generator

const HTTP_CONFLICT = 409
const FAILURES_BY_CODE: Record<string, PollVoteFailure> = {
  POLL_CLOSED: 'closed',
  ALREADY_VOTED: 'already_voted',
}

export const boardPollsActions = {
  LOAD_VOTES: 'COMMUNICATION_BOARD/POLLS/LOAD_VOTES',
  SEND_VOTE: 'COMMUNICATION_BOARD/POLLS/SEND_VOTE',
}

const isApiError = (error: unknown): error is PollApiError => error instanceof Error && 'status' in error

const failureOf = (error: unknown): PollVoteFailure => {
  if (!isApiError(error) || error.status !== HTTP_CONFLICT) return 'error'
  return FAILURES_BY_CODE[error.responseData?.error?.code ?? ''] ?? 'error'
}

const myVotesRequest = (endpoint: string, pollKeys: string[]) =>
  call(requestApi, {
    endpoint,
    queryParams: pollKeys.map(pollKey => ['poll_keys[]', pollKey]),
    showErrorSnackbar: false,
  })

export function* savedScoreOf(endpoint: string | undefined, pollKey: string): Generator<unknown, PollScore | null> {
  if (!endpoint) {
    return null
  }

  try {
    const response = (yield myVotesRequest(endpoint, [pollKey])) as MyPollVotesResponse
    return response?.data?.votes?.[pollKey] ?? null
  } catch {
    // Already reported by callApi: the thank-you line falls back to the face the agent picked.
    return null
  }
}

export function* loadPollVotes({ pollKeys }: LoadPollVotesAction): Generator {
  const { communicationBoardPollVotesUrl } = (yield select(selectExtraRoutesAuthData)) as BoardPollRoutes
  const { loadedKeys } = (yield select(selectBoardPollsState)) as BoardPollsState
  const missingKeys = pollKeys.filter(pollKey => !loadedKeys.includes(pollKey))

  if (!communicationBoardPollVotesUrl || missingKeys.length === 0) {
    return
  }

  try {
    const response = (yield myVotesRequest(communicationBoardPollVotesUrl, missingKeys)) as MyPollVotesResponse

    yield put(votesLoaded({ pollKeys: missingKeys, votes: response?.data?.votes ?? {} }))
  } catch {
    // Already reported by callApi: the form stays open and the API refuses a second vote anyway.
    yield put(votesLoaded({ pollKeys: missingKeys, votes: {} }))
  }
}

export function* sendPollVote({ pollKey }: SendPollVoteAction): Generator {
  const { communicationBoardPollVoteUrl, communicationBoardPollVotesUrl } = (yield select(
    selectExtraRoutesAuthData,
  )) as BoardPollRoutes
  const { drafts, sendingKeys } = (yield select(selectBoardPollsState)) as BoardPollsState
  const score = drafts[pollKey]?.score ?? null
  const comment = (drafts[pollKey]?.comment ?? '').trim()

  const cannotSend = !communicationBoardPollVoteUrl || score === null || sendingKeys.includes(pollKey)
  if (cannotSend) {
    return
  }

  yield put(voteRequested(pollKey))

  try {
    yield call(requestApi, {
      endpoint: communicationBoardPollVoteUrl,
      method: 'POST',
      payload: { score, comment: comment || null },
      asteriskReplacement: pollKey,
      showErrorSnackbar: false,
    })
    yield put(voteSucceeded({ pollKey, score }))
  } catch (error: unknown) {
    const failure = failureOf(error)
    const savedScore =
      failure === 'already_voted'
        ? ((yield call(savedScoreOf, communicationBoardPollVotesUrl, pollKey)) as PollScore | null)
        : null
    yield put(voteFailed({ pollKey, score: savedScore ?? score, failure }))
  }
}

export default function* boardPollsSagas(): Generator {
  yield takeEvery(boardPollsActions.LOAD_VOTES, loadPollVotes)
  yield takeEvery(boardPollsActions.SEND_VOTE, sendPollVote)
}
